import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SelectInputField } from '@/components/form/SelectInput';
import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { DateField } from '@/components/shared/date/DateInput';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { breadcrumbName } from '@/helpers/Utility';
import { dateToApiFormat, parseDateOnly } from '@/helpers/dateHelper';
import { useAvailabilityTypes } from '@/services/availability/useAvailabilityTypes';
import { useEmployeeLeaveRequestPreview } from '@/services/availability/useEmployeeLeaveRequestPreview';
import { useManagerBookLeave } from '@/services/availability/useManagerBookLeave';
import { useUnsavedChangesWarning } from '@/services/confirm/ConfirmationService';
import {
  LeaveAllowancePeriod,
  ReadEmployee,
  useGetEmployeeLeaveAllowancesQuery,
  useGetStaffByIdQuery
} from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { LeaveAllowanceCheck } from '@/shared/leave/LeaveAllowanceCheck';
import { LeaveOverlapNotice } from '@/shared/leave/LeaveOverlapNotice';
import { startOfDay } from 'date-fns';
import { ComponentProps, FC, Suspense, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useNavigate, useParams } from 'react-router-dom';
import dayStrings from '../../team.translations';
import { TeamMemberHeader } from '../TeamMemberHeader';
import { TeamMemberProvider } from '../TeamMemberService';

const managerLeaveMessages: ComponentProps<typeof LeaveAllowanceCheck>['messages'] = {
  daysToTake: strings.common.daysToTake,
  exceededSeverity: 'warning',
  exceeded: (c, label) => strings.timeshift.teamMember.leaveBalances.overAllowance(label, Math.abs(c.remaining)),
  remaining: (c, label) => strings.timeshift.teamMember.leaveBalances.remainingAfter(c.remaining, label)
};

interface ManagerLeaveFormValues {
  availabilityType: string;
  start: Date;
  end: Date;
}

const TeamMemberCalendarAddLeaveForm: FC<{ employeeId: string; selectedDate: Date }> = ({
  employeeId,
  selectedDate
}) => {
  const navigate = useNavigate();

  const availabilityTypes = useAvailabilityTypes(true);
  const leaveTypes = useMemo(() => availabilityTypes.filter(type => type.leaveTypeId), [availabilityTypes]);

  const methods = useForm<ManagerLeaveFormValues>({
    defaultValues: { availabilityType: '', start: selectedDate, end: selectedDate }
  });
  const [availabilityType, start, end] = useWatch({
    control: methods.control,
    name: ['availabilityType', 'start', 'end']
  });
  const isInvalidRange = start > end;

  const setStart = (date?: Date) => {
    if (date) methods.setValue('start', date, { shouldDirty: true });
  };
  const setEnd = (date?: Date) => {
    if (date) methods.setValue('end', date, { shouldDirty: true });
  };
  const syncEndToStart = (date?: Date) => {
    if (date && startOfDay(date) > startOfDay(end)) setEnd(date);
  };
  const syncStartToEnd = (date?: Date) => {
    if (date && startOfDay(date) < startOfDay(start)) setStart(date);
  };

  const {
    hasPreview,
    checks,
    daysToTake,
    fetching: previewLoading,
    exceeded,
    overlappingDates,
    hasOverlap
  } = useEmployeeLeaveRequestPreview(employeeId, availabilityType, start, end);
  const exceededChecks = useMemo(() => checks.filter(check => check.exceeds), [checks]);

  const [{ data: yearly }] = useGetEmployeeLeaveAllowancesQuery({
    variables: { employeeId, period: LeaveAllowancePeriod.PerYear, periodStart: dateToApiFormat(start) }
  });

  const options = useMemo(() => {
    const remainingByType = new Map((yearly?.employeeLeaveAllowances ?? []).map(a => [a.leaveType.id, a.remaining]));

    return leaveTypes.map(type => {
      const remaining = remainingByType.get(type.leaveTypeId);
      return {
        name: remaining === undefined ? type.name : `${type.name} (${strings.common.daysRemaining(remaining)})`,
        value: type.leaveTypeId
      };
    });
  }, [leaveTypes, yearly]);

  const { bookLeave, fetching } = useManagerBookLeave(employeeId);
  const [warningOpen, setWarningOpen] = useState(false);

  useUnsavedChangesWarning(methods.formState.isDirty);

  const submitBooking = async () => {
    if (!availabilityType) return;

    const saved = await bookLeave(start, end, availabilityType);
    if (!saved) return;

    methods.reset(methods.getValues());
    navigate(`/team-member/${employeeId}/calendar-details/${dateToApiFormat(selectedDate)}`);
  };

  const onSubmit = async () => {
    if (isInvalidRange || hasOverlap) return;

    if (exceeded) {
      setWarningOpen(true);
      return;
    }

    await submitBooking();
  };

  return (
    <>
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-5">
            <div className="flex flex-row space-x-5">
              <DateField
                id="start"
                name="start"
                label={strings.common.startDate}
                required
                value={start}
                onChange={setStart}
                onChangeCallback={syncEndToStart}
              />
              <DateField
                id="end"
                name="end"
                label={strings.common.endDate}
                required
                value={end}
                onChange={setEnd}
                onChangeCallback={syncStartToEnd}
              />
            </div>
            <SelectInputField
              id="availabilityType"
              name="availabilityType"
              label={strings.timeshift.teamMember.calendarDayView.editAvailability.availabilityLeaveType}
              options={options}
              required
            />

            {availabilityType && hasOverlap && (
              <LeaveOverlapNotice
                dates={overlappingDates}
                title={strings.timeshift.teamMember.leaveBalances.overlapBlocked}
              />
            )}

            {availabilityType && hasPreview && daysToTake > 0 && (
              <LeaveAllowanceCheck checks={checks} daysToTake={daysToTake} messages={managerLeaveMessages} />
            )}
            <div className="flex w-full items-center justify-end gap-5">
              <Button className="flex-1" onClick={() => navigate(-1)}>
                {strings.common.cancel}
              </Button>
              <Button
                className="flex-1"
                primary
                type="submit"
                loading={fetching}
                disabled={!availabilityType || previewLoading || isInvalidRange || hasOverlap}
              >
                {strings.timeshift.teamMember.calendar.bookLeave}
              </Button>
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
      <ConfirmDialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        onConfirm={async () => {
          setWarningOpen(false);
          await submitBooking();
        }}
        title={strings.timeshift.teamMember.leaveBalances.warningTitle}
        confirmLabel={strings.timeshift.teamMember.leaveBalances.confirmLabel}
        cancelLabel={strings.common.cancel}
        loading={fetching}
      >
        <div className="space-y-3">
          <p>{strings.timeshift.teamMember.leaveBalances.warningMessage}</p>
          <LeaveAllowanceCheck checks={exceededChecks} daysToTake={daysToTake} messages={managerLeaveMessages} />
          <p>{strings.timeshift.teamMember.leaveBalances.warningContinue}</p>
        </div>
      </ConfirmDialog>
    </>
  );
};

const TeamMemberCalendarAddLeave: FC = () => {
  const { userId, date } = useParams<'userId' | 'date'>();
  if (!userId || !date) throw Error('Employee id and date should be provided in url params');

  const selectedDate = useMemo(() => parseDateOnly(date), [date]);
  const [{ data, error }] = useGetStaffByIdQuery({ variables: { id: userId } });
  const employee = data?.employee as ReadEmployee | undefined;
  const calendarPath = `/team-member/${userId}/calendar-details/${date}`;
  const breadcrumbs = [
    { name: dayStrings.teamPageTitle, to: '/team' },
    {
      name: employee ? breadcrumbName(employee, dayStrings.employeeName) : dayStrings.employeeName('', ''),
      to: `/team-member/${userId}`
    },
    {
      name: <span>{strings.timeshift.teamMember.leaveBalances.requestLeave}</span>,
      to: calendarPath
    }
  ];

  if (error) throw error;

  return (
    <TeamMemberProvider>
      <PageContainer customHeader={<TeamMemberHeader employee={employee} breadcrumbs={breadcrumbs} />}>
        <Suspense fallback={<TableLoader />}>
          <PageSizer mode={SizerMode.PAGE} className="justify-items-center">
            <TSTitleCard
              className="w-full space-y-5 p-5"
              title={strings.timeshift.teamMember.leaveBalances.requestLeave}
              icon={AiOutlineCalendar}
            >
              <TeamMemberCalendarAddLeaveForm employeeId={userId} selectedDate={selectedDate} />
            </TSTitleCard>
          </PageSizer>
        </Suspense>
      </PageContainer>
    </TeamMemberProvider>
  );
};

export default TeamMemberCalendarAddLeave;
